/**
 * Solo battles on the server (design D4, server side; stage 2 contract 5) and the grave search after them: the
 * player.command ops 'battle.start', 'battle.finish', 'battle.forfeit' and 'grave.search' (BattleService.registerOps).
 * They run through player.command (./commands.ts), so the opId journal, the per-player lock and the single document
 * write apply to them: a battle.finish retried after a lost reply is answered from the journal and never rewards (or
 * simulates) twice, and a failed command leaves neither the state nor the server state changed.
 *
 * battle.start { mode, stage | zone, teamName? }
 *   SERVER op battle.start (prepare_game + the time refresh + init_game's team + the seed) with the server's facts:
 *   an assault's zone stage and owner from AssaultService.zoneFacts, the auction annihilators from the annihilator
 *   source (0 until the auction stage: setAnnihilatorSource). The battle becomes the player's ONE pending battle,
 *   serverState[PENDING_BATTLE_KEY] = { battleId, seed, basis, init, startedAt } with init = the SoloBattleInit the
 *   simulator re-plays (the canonical state right AFTER the start, stims taken, annihilators, team name, mode, stage,
 *   zone stage). A battle still pending is ended as a loss first (its start costs stay, as in the original, where a
 *   closed window lost the battle); the graves of the previous battle are gone (the grave search screen is closed).
 *
 * battle.finish { battleId, claim, log }
 *   Needs the matching pending battle (INVALID_STATE 'no_pending_battle' otherwise). The claim is the rules'
 *   BattleOutcome, the log a src/session CommandLog in global battle ticks (both validated: VALIDATION_FAILED
 *   'bad_args', the battle stays pending). With a simulator the server re-plays BattleRecord { init, seed, log } and
 *   never falls back to the claim:
 *     'verified' = the simulated outcome equals the claim (winner, counters, graves);
 *     'rejected' = it differs, and the SIMULATED outcome is applied; or the record cannot be re-simulated at all
 *       (SimulationError 'invalid_record': input the game could not have produced, a log the battle does not accept;
 *       'max_ticks': the battle does not end within the mode's tick limit), which the client caused: the battle is
 *       LOST (winner 0, nothing granted);
 *     no verdict = the simulator could not answer ('timeout', 'crashed', 'unavailable', no answer within simWaitMs,
 *       a malformed outcome): UNAVAILABLE 'simulation_unavailable', which is not journaled, so the battle stays
 *       pending and the client retries with the same opId. A retry of the same battle and log waits for the
 *       simulation already running (or takes the answer it gave in the meantime) instead of queueing another one.
 *   'unverified' = no simulator is configured (VALIDATOR=stub, the in-process backend): the claim is applied.
 *   A battle older than ABANDONED_BATTLE_MS is a loss whatever the finish says ('rejected', refused 'abandoned',
 *   nothing granted, not simulated). Then SERVER op battle.result { outcome, basis of the start }:
 *   'battle_state_changed' (the stage progress changed since the start: progress.startOver) and 'battle_mismatch'
 *   (the outcome is another battle) give no reward but end the battle. A won battle that is not an assault leaves
 *   its graves for grave.search (serverState[LAST_BATTLE_KEY]). An assault win captures the zone through
 *   AssaultService.captureAfterBattle when it is verified, or unverified while config.assault.requireVerifiedBattle is
 *   off; a lost race (the zone's stage moved on, or it is the player's / the clan's by now) keeps the rewards and
 *   reports captured:false. The battle is stored as a replay (its id is in the result). The new state, the replay
 *   and the capture (zone + account) are ONE atomic storage batch (ServiceOpOutcome.commit).
 *
 * battle.forfeit { battleId }: the pending battle ends as a loss (nothing is given back).
 *
 * grave.search { index }: SERVER op grave.search with the graves of the last won battle; each grave once.
 *
 * Abandoned battles: a pending battle older than ABANDONED_BATTLE_MS (6 h) is a loss at the next battle.start,
 * profile.load (expireAbandoned), battle.finish (see above) or battle.forfeit (which ends any battle as a loss).
 */
import { sha256Hex } from '../../../shared/crypto/sha256';
import { type JsonObject, type JsonValue, cloneJson, isPlainObject } from '../../../shared/json';
import { randomId } from '../../../shared/random';
import { BackendError } from '../../../shared/protocol/errors';
import type {
  BattleFinishCommandResult,
  BattleForfeitCommandResult,
  BattleGranted,
  BattleOutcomeClaim,
  BattleStartCommandResult,
  PlayerId,
  ValidationVerdict,
} from '../../../shared/protocol/model';
import { type CommandLogData, validateCommandLogData } from '../../../session/command-log';
import { canonicalJson } from '../../../session/hash';
import { RuleError, argsObject, decodeState, intArg, isRuleError, optionalStringArg, stringArg } from '../../../game/rules/index';
import {
  ASSAULT_ZONES,
  BATTLE_START_MODES,
  type BattleOutcome,
  type BattleResultResult,
  type BattleRewardBasis,
  type BattleStartMode,
  type BattleStartResult,
  parseBattleOutcome,
} from '../../../game/rules/ops/battle';
import type { GraveResult } from '../../../game/rules/ops/progress-claims';
import type { BattleRecord, BattleSimulator, SimulationError, SimulationResult, SoloBattleInit, SoloBattleMode } from '../battle-sim';
import type { ServerState } from '../rules-runtime';
import type { StorageBatchOp } from '../storage';
import type { AssaultService, CaptureBlocked } from './assault';
import type { CommandService, ServiceOpContext, ServiceOpOutcome } from './commands';
import type { ServiceDeps } from './deps';
import type { ProfileService } from './profile';
import type { BattleReplayWrites, ReplayService } from './replays';

/** serverState key of the in-flight battle (PendingBattle). */
export const PENDING_BATTLE_KEY = 'pendingBattle';
/** serverState key of the graves of the last won battle (LastBattle). */
export const LAST_BATTLE_KEY = 'lastBattle';
/**
 * serverState key of the last finished battle when it was won (LastWin): what the opt-in ad 'ad_battle_double' may
 * double (services/ads.ts). A lost or abandoned battle removes it.
 */
export const LAST_WIN_KEY = 'lastWin';

/** serverState[LAST_WIN_KEY]. */
export interface LastWin {
  battleId: string;
  mode: string;
  stage: number;
  /** The gold the victory granted. */
  gold: number;
}

/** The last won battle of a server state (malformed entries count as none). */
export function lastWinOf(s: ServerState | undefined): LastWin | null {
  const v = s?.[LAST_WIN_KEY];
  if (!isPlainObject(v) || typeof v.battleId !== 'string' || typeof v.mode !== 'string' || typeof v.stage !== 'number' || typeof v.gold !== 'number') return null;
  return v as unknown as LastWin;
}
/** A pending battle older than this is abandoned: a loss at the next battle.start or profile.load. */
export const ABANDONED_BATTLE_MS = 6 * 3600_000;
/** Wall-clock budget of one re-simulation (the simulator terminates it after this). */
export const BATTLE_SIM_TIMEOUT_MS = 60_000;
/**
 * Extra wait for the simulator's answer on top of its budget (a queue in front of busy workers); after it battle.finish
 * answers UNAVAILABLE and the simulation goes on for the retry.
 */
export const BATTLE_SIM_WAIT_GRACE_MS = 15_000;
/** How long a simulator answer that came after battle.finish stopped waiting is kept for the retry. */
export const BATTLE_SIM_ANSWER_KEEP_MS = 10 * 60_000;
/**
 * Simulation errors that are the record's own (the same record always fails the same way): the battle is lost.
 * The others ('timeout', 'crashed', 'unavailable') are the server's: the battle stays pending for a retry.
 */
export const RECORD_SIM_ERRORS: ReadonlySet<SimulationError> = new Set<SimulationError>(['invalid_record', 'max_ticks']);
/**
 * Battle ticks (50 per second of battle time) a re-simulation may run per mode; a log that ends later is refused.
 * Survival is a series of waves, hence twice the time.
 */
export const BATTLE_MAX_TICKS: Readonly<Record<SoloBattleMode, number>> = {
  career: 400_000,
  missions: 400_000,
  boss: 400_000,
  assault: 400_000,
  survival: 720_000,
};

/** battle ids (b_ + base32). */
const BATTLE_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/** serverState[PENDING_BATTLE_KEY]: the player's battle in progress. */
export interface PendingBattle {
  battleId: string;
  /** Battle seed 0..399 (battle.start). */
  seed: number;
  /** The reward basis battle.start froze (battle.result reads difficulty, perks and progress from it). */
  basis: BattleRewardBasis;
  /** What the battle is re-simulated from (state right after the start). */
  init: SoloBattleInit;
  /** Server time of the start, epoch ms. */
  startedAt: number;
}

/** A grave of the last won battle and whether it was searched. */
export interface LastBattleGrave {
  name: string;
  cpu_level: number;
  searched: boolean;
}

/** serverState[LAST_BATTLE_KEY]: the graves the grave search screen shows after a won battle. */
export interface LastBattle {
  battleId: string;
  mode: string;
  stage: number;
  graves: LastBattleGrave[];
}

/**
 * HOOK for the auction stage (design D7, "king of the hill" annihilator auction): how many auction annihilators the
 * player holds for a battle that starts now. The default source gives 0.
 */
export type AnnihilatorSource = (playerId: PlayerId) => number | Promise<number>;

export const noAuctionAnnihilators: AnnihilatorSource = () => 0;

export interface BattleServiceOptions {
  /**
   * Re-simulates finished battles; none = every battle is 'unverified' (the claim is applied). With one, the claim is
   * never applied unverified: a battle the simulator cannot answer for stays pending (UNAVAILABLE).
   */
  simulator?: BattleSimulator | null;
  /** Budget of one simulation (SimulateOptions.timeoutMs); default BATTLE_SIM_TIMEOUT_MS. */
  simTimeoutMs?: number;
  /**
   * How long one battle.finish waits for the simulator at most (then UNAVAILABLE; the simulation goes on for the
   * retry); default simTimeoutMs + BATTLE_SIM_WAIT_GRACE_MS.
   */
  simWaitMs?: number;
  /** Tick limits per mode; default BATTLE_MAX_TICKS. */
  maxTicks?: Partial<Record<SoloBattleMode, number>>;
  annihilators?: AnnihilatorSource;
}

/** The pending battle of a server state (malformed entries count as none). */
export function pendingBattleOf(s: ServerState | undefined): PendingBattle | null {
  const v = s?.[PENDING_BATTLE_KEY];
  if (!isPlainObject(v)) return null;
  if (typeof v.battleId !== 'string' || typeof v.seed !== 'number' || typeof v.startedAt !== 'number' || !isPlainObject(v.init) || !isPlainObject(v.basis)) return null;
  return v as unknown as PendingBattle;
}

/** The graves of the last won battle of a server state (malformed entries count as none). */
export function lastBattleOf(s: ServerState | undefined): LastBattle | null {
  const v = s?.[LAST_BATTLE_KEY];
  if (!isPlainObject(v)) return null;
  if (typeof v.battleId !== 'string' || typeof v.mode !== 'string' || typeof v.stage !== 'number' || !Array.isArray(v.graves)) return null;
  const graves = v.graves as unknown[];
  if (!graves.every((g) => isPlainObject(g) && typeof g.name === 'string' && typeof g.cpu_level === 'number' && typeof g.searched === 'boolean')) return null;
  return v as unknown as LastBattle;
}

/** A copy of the server state without the pending battle and the last battle's graves. */
function withoutBattles(s: ServerState): ServerState {
  const next = { ...s };
  delete next[PENDING_BATTLE_KEY];
  delete next[LAST_BATTLE_KEY];
  return next;
}

// The protocol's claim shape (src/shared/protocol/model.ts) is the rules' BattleOutcome.
type Same<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
const claimShapeIsRuleOutcome: Same<BattleOutcome, BattleOutcomeClaim> = true;
void claimShapeIsRuleOutcome;

const ZERO_KILLS = { p: 0, e: 0, a: 0, c: 0, h: 0, f: 0, l: 0 };

/** An outcome with the defaults of the fields a loss may leave out (what "equal outcomes" compares). */
function comparable(o: BattleOutcome): JsonValue {
  return {
    winner: o.winner,
    mode: o.mode,
    stage: o.stage ?? null,
    zone: o.zone ?? null,
    ach_killed: o.ach_killed ?? ZERO_KILLS,
    ach_paralyzed: o.ach_paralyzed ?? 0,
    ach_annihilated: o.ach_annihilated ?? 0,
    ach_injure: o.ach_injure ?? false,
    ach_kill: o.ach_kill ?? false,
    graves: (o.graves ?? []).map((g) => ({ name: g.name, cpu_level: g.cpu_level })),
  };
}

export function sameOutcome(a: BattleOutcome, b: BattleOutcome): boolean {
  return canonicalJson(comparable(a)) === canonicalJson(comparable(b));
}

/** The outcome is the pending battle's (mode and stage, the zone of an assault). */
function isBattleOf(o: BattleOutcome, init: SoloBattleInit): boolean {
  if (o.mode !== init.mode) return false;
  return init.mode === 'assault' ? o.zone === init.stage : o.stage === init.stage;
}

/** The pending battle lost (winner 0, no counters): what a record the server refuses and an abandoned battle give. */
function lostBattle(init: SoloBattleInit): BattleOutcome {
  return init.mode === 'assault' ? { winner: 0, mode: 'assault', zone: init.stage } : { winner: 0, mode: init.mode, stage: init.stage };
}

/** battle.finish cannot be decided now: not journaled, the battle stays pending, the client retries (same opId). */
function simulationUnavailable(battleId: string, error: SimulationError | 'no_answer'): BackendError {
  return new BackendError('UNAVAILABLE', 'The battle cannot be verified right now, try again', { reason: 'simulation_unavailable', battleId, error });
}

function badArgs(field: string, problem: string): never {
  throw new RuleError('bad_args', { field, problem });
}

/** The claim as the rules parse it; bad fields are reported as 'claim.<field>'. */
function parseClaim(raw: unknown): BattleOutcome {
  if (raw === undefined || raw === null) badArgs('claim', 'required');
  try {
    return parseBattleOutcome(raw);
  } catch (err) {
    if (isRuleError(err) && err.reason === 'bad_args') {
      const f = err.details.field;
      throw new RuleError('bad_args', { ...err.details, field: `claim${f ? '.' + String(f) : ''}` });
    }
    throw err;
  }
}

/** The battle's command log (src/session validator), ending within the mode's tick limit. */
function parseLog(raw: unknown, maxTicks: number): CommandLogData {
  if (raw === undefined || raw === null) badArgs('log', 'required');
  const problem = validateCommandLogData(raw);
  if (problem) badArgs('log', problem);
  const log = raw as CommandLogData;
  const last = log.turns[log.turns.length - 1];
  const end = last.endTick ?? last.throughTick;
  if (end > maxTicks) badArgs('log', `ends at tick ${end}, beyond the battle limit of ${maxTicks} ticks`);
  return cloneJson(log);
}

const NOTHING_GRANTED: BattleGranted = { gold: 0, exp: 0, rating: 0, levels: 0, diam: 0, skillPts: 0, perkPts: 0, weapons: [] };

interface Verification {
  verdict: ValidationVerdict;
  /** The outcome to apply: the simulated one, a loss (a refused record), or the claim when unverified. */
  outcome: BattleOutcome;
  /** What the simulator answered (none: unverified or abandoned). */
  sim?: { ticks: number | null; error: SimulationError | null; message: string | null; simulated: BattleOutcome | null };
}

/**
 * What a finished solo battle was (BattleService.onFinished, after it is stored): the anti-cheat's view of it. `claim` and
 * `simulated` are compared the way the verdict compares them (winner, counters, graves); `differs` names the fields.
 */
export interface BattleFinishFacts {
  playerId: PlayerId;
  battleId: string;
  replayId: string;
  mode: SoloBattleMode;
  stage: number;
  verdict: ValidationVerdict;
  /** The battle was older than ABANDONED_BATTLE_MS: lost whatever was sent (nothing simulated). */
  abandoned: boolean;
  refused: string | null;
  win: boolean;
  winner: number;
  /** The gold and diamonds the battle granted. */
  granted: { gold: number; diam: number };
  claim: JsonValue;
  simulated: JsonValue | null;
  differs: string[];
  /** The simulator's error ('invalid_record', 'max_ticks') and message, when it refused the record. */
  simError: SimulationError | null;
  simMessage: string | null;
  /** Battle ticks the simulation ran, else the last tick of the log. */
  ticks: number;
  simulatedTicks: boolean;
  /** Turns of the player's team in the log, and the enemy worms it killed (the outcome applied). */
  ownTurns: number;
  kills: number;
  annihilators: number;
  /** basis: the battle was at the mode's frontier stage (a new stage, not a replay), and its attempt number. */
  newStage: boolean;
  attempt: number | null;
  startedAt: number;
  finishedAt: number;
  /** A simulator is configured (without one every battle is 'unverified'). */
  simulator: boolean;
}

export type BattleFinishListener = (facts: BattleFinishFacts) => void;

/**
 * A solo battle that ended as a loss WITHOUT battle.finish (BattleService.onLost, after it is stored): the surrender
 * button (battle.forfeit: the game's client never sends battle.finish for a surrendered battle), a new battle.start
 * while it was pending (replaced, or abandoned when it was older than ABANDONED_BATTLE_MS), or profile.load ending an
 * abandoned one. The anti-cheat counts it as the loss it is (a win streak ends with it).
 */
export interface BattleLostFacts {
  playerId: PlayerId;
  battleId: string;
  mode: SoloBattleMode;
  stage: number;
  how: 'forfeited' | 'replaced' | 'abandoned';
  /** The battle was at the mode's frontier stage (see BattleFinishFacts.newStage). */
  newStage: boolean;
  startedAt: number;
  at: number;
}

export type BattleLostListener = (facts: BattleLostFacts) => void;

/** The battle is at its mode's frontier stage (*_max: the next one to win), not a replay of a won one or an assault. */
function atFrontier(p: PendingBattle): boolean {
  const basis = p.basis as BattleRewardBasis & { max?: number };
  return typeof basis.max === 'number' && p.init.mode !== 'assault' ? p.init.stage === basis.max : false;
}

export class BattleService {
  private readonly simulator: BattleSimulator | null;
  private readonly simTimeoutMs: number;
  private readonly simWaitMs: number;
  private readonly maxTicks: Record<SoloBattleMode, number>;
  private annihilatorSource: AnnihilatorSource;
  /**
   * Simulations by battle and log (`battleId:sha256(log)`), from the start until a battle.finish takes the answer
   * (or BATTLE_SIM_ANSWER_KEEP_MS after the answer came without anyone waiting): a retried finish joins the run.
   */
  private readonly simulations = new Map<string, Promise<SimulationResult>>();
  private readonly finishListeners = new Set<BattleFinishListener>();
  private readonly lostListeners = new Set<BattleLostListener>();

  constructor(
    private readonly d: ServiceDeps,
    private readonly profiles: ProfileService,
    private readonly assault: AssaultService,
    private readonly replays: ReplayService,
    opts: BattleServiceOptions = {},
  ) {
    this.simulator = opts.simulator ?? null;
    this.simTimeoutMs = opts.simTimeoutMs ?? BATTLE_SIM_TIMEOUT_MS;
    this.simWaitMs = opts.simWaitMs ?? this.simTimeoutMs + BATTLE_SIM_WAIT_GRACE_MS;
    this.maxTicks = { ...BATTLE_MAX_TICKS, ...(opts.maxTicks ?? {}) };
    this.annihilatorSource = opts.annihilators ?? noAuctionAnnihilators;
  }

  /** Calls `l` with the facts of every finished solo battle once it is stored (the anti-cheat). */
  onFinished(l: BattleFinishListener): () => void {
    this.finishListeners.add(l);
    return () => this.finishListeners.delete(l);
  }

  /** Calls `l` with every solo battle that ended as a loss without battle.finish, once it is stored (the anti-cheat). */
  onLost(l: BattleLostListener): () => void {
    this.lostListeners.add(l);
    return () => this.lostListeners.delete(l);
  }

  private lost(playerId: PlayerId, p: PendingBattle, how: BattleLostFacts['how'], at: number): void {
    const f: BattleLostFacts = { playerId, battleId: p.battleId, mode: p.init.mode, stage: p.init.stage, how, newStage: atFrontier(p), startedAt: p.startedAt, at };
    for (const l of this.lostListeners) {
      try {
        l(f);
      } catch (err) {
        this.d.logger.error('battle lost listener failed', { playerId, battleId: p.battleId, err });
      }
    }
  }

  /** The simulator's name for /health and the banner (null = none: battles stay unverified). */
  get simulatorMode(): string | null {
    return this.simulator?.mode ?? null;
  }

  /**
   * Where battle.start takes the player's Annihilators from (default: none). The backend core wires the annihilator
   * auction here (AuctionService.annihilatorsOf: the holder's copy + the permanent copies of won rounds, design D7).
   */
  setAnnihilatorSource(source: AnnihilatorSource): void {
    this.annihilatorSource = source;
  }

  /** Makes battle.start / battle.finish / battle.forfeit / grave.search client-callable commands. */
  registerOps(commands: CommandService): void {
    commands.registerServiceOp('battle.start', (ctx, args) => this.start(ctx, args));
    commands.registerServiceOp('battle.finish', (ctx, args) => this.finish(ctx, args));
    commands.registerServiceOp('battle.forfeit', (ctx, args) => this.forfeit(ctx, args));
    commands.registerServiceOp('grave.search', (ctx, args) => this.searchGrave(ctx, args));
  }

  private isAbandoned(p: PendingBattle, nowMs: number): boolean {
    return nowMs - p.startedAt > ABANDONED_BATTLE_MS;
  }

  /**
   * profile.load: an abandoned pending battle (older than ABANDONED_BATTLE_MS) ends as a loss (nothing to apply: a
   * lost battle changes no state). Returns its battleId, or null when there was none. Writes the profile document only
   * when it removes one (no new revision: the game state is unchanged).
   */
  async expireAbandoned(playerId: PlayerId): Promise<string | null> {
    const peek = await this.profiles.getRecord(playerId);
    const seen = pendingBattleOf(peek?.serverState);
    if (!seen || !this.isAbandoned(seen, this.d.clock.now())) return null;
    return this.profiles.withRecord(playerId, async (rec, a) => {
      const p = pendingBattleOf(rec.serverState);
      if (!p || !this.isAbandoned(p, this.d.clock.now())) return null;
      rec.serverState = withoutBattles(rec.serverState ?? {});
      await this.profiles.storeRecord(rec, a, false);
      this.d.logger.info('abandoned battle ended as a loss', { playerId, battleId: p.battleId, mode: p.init.mode, stage: p.init.stage, startedAt: p.startedAt });
      this.lost(playerId, p, 'abandoned', this.d.clock.now());
      return p.battleId;
    });
  }

  private async annihilatorsFor(playerId: PlayerId): Promise<number> {
    const n = Math.floor(Number(await this.annihilatorSource(playerId)));
    return Number.isFinite(n) ? Math.max(0, Math.min(1000, n)) : 0;
  }

  // ---------------------------------------------------------------------------------------------- battle.start

  private async start(ctx: ServiceOpContext, args: JsonObject): Promise<ServiceOpOutcome> {
    const o = argsObject(args, ['mode', 'stage', 'zone', 'teamName']);
    const mode = stringArg<BattleStartMode>(o, 'mode', { oneOf: BATTLE_START_MODES });
    const teamName = optionalStringArg(o, 'teamName', { maxLength: 64 }) ?? ctx.account.displayName;
    const annihilators = await this.annihilatorsFor(ctx.playerId);
    const ruleArgs: JsonObject = { mode, teamName, annihilators };
    if (mode === 'assault') {
      if (o.stage !== undefined) badArgs('stage', 'an assault has a zone, not a stage');
      const zone = intArg(o, 'zone', { min: 1, max: ASSAULT_ZONES });
      const facts = await this.assault.zoneFacts(ctx.account, zone);
      Object.assign(ruleArgs, { zone, zoneStage: facts.stage, zoneOwner: facts.owner });
    } else {
      if (o.zone !== undefined) badArgs('zone', 'only an assault has a zone');
      ruleArgs.stage = intArg(o, 'stage', { min: 1, max: 1000 });
    }

    // One battle at a time: a pending one ends as a loss (its start costs stay); its graves are gone too.
    const previous = pendingBattleOf(ctx.serverState);
    const endedBattle = previous ? { battleId: previous.battleId, reason: this.isAbandoned(previous, ctx.nowMs) ? ('abandoned' as const) : ('replaced' as const) } : null;
    ctx.setServerState(withoutBattles(ctx.serverState));

    const r = ctx.execute('battle.start', ruleArgs) as unknown as BattleStartResult;
    // BATTLE_SEED (a test and development switch): the rules drew a seed as usual, the battle takes the fixed one.
    const seed = this.d.fixedBattleSeed ?? r.seed;
    const battleId = randomId(this.d.rng, 'b_', 10);
    const init: SoloBattleInit = {
      kind: 'solo',
      mode: r.mode,
      stage: r.stage,
      ...(r.zoneStage !== undefined ? { zoneStage: r.zoneStage } : {}),
      gameState: cloneJson(ctx.gameState),
      stimsInBattle: cloneJson(r.stimsInBattle),
      annihilators,
      teamName,
    };
    const pending: PendingBattle = { battleId, seed, basis: r.basis, init, startedAt: ctx.nowMs };
    ctx.setServerState({ ...ctx.serverState, [PENDING_BATTLE_KEY]: pending as unknown as JsonValue });
    if (endedBattle) this.d.logger.info('pending battle ended as a loss by a new start', { playerId: ctx.playerId, ...endedBattle });

    const result: BattleStartCommandResult = {
      battleId,
      mode: r.mode,
      stage: r.stage,
      ...(r.zoneStage !== undefined ? { zoneStage: r.zoneStage } : {}),
      seed,
      stimsInBattle: r.stimsInBattle,
      annihilators,
      teamName,
      papaHere: r.papaHere,
      tired: r.tired,
      ...(endedBattle ? { endedBattle } : {}),
    };
    const playerId = ctx.playerId;
    const nowMs = ctx.nowMs;
    return { result: result as unknown as JsonValue, ...(previous && endedBattle ? { afterCommit: () => this.lost(playerId, previous, endedBattle.reason, nowMs) } : {}) };
  }

  // ---------------------------------------------------------------------------------------------- battle.finish

  private requirePending(ctx: ServiceOpContext, battleId: string): PendingBattle {
    const p = pendingBattleOf(ctx.serverState);
    if (!p || p.battleId !== battleId) {
      throw new BackendError('INVALID_STATE', 'This battle is not in progress', { reason: 'no_pending_battle', battleId, pending: p?.battleId ?? null });
    }
    return p;
  }

  private maxTicksFor(mode: SoloBattleMode): number {
    return this.maxTicks[mode] ?? BATTLE_MAX_TICKS.career;
  }

  private async finish(ctx: ServiceOpContext, args: JsonObject): Promise<ServiceOpOutcome> {
    const o = argsObject(args, ['battleId', 'claim', 'log']);
    const battleId = stringArg(o, 'battleId', { maxLength: 64, pattern: BATTLE_ID_PATTERN });
    const pending = this.requirePending(ctx, battleId);
    const claim = parseClaim(o.claim);
    const log = parseLog(o.log, this.maxTicksFor(pending.init.mode));
    // An abandoned battle is a loss whatever the finish says (as at the next profile.load or battle.start).
    const abandoned = this.isAbandoned(pending, ctx.nowMs);
    const checked: Verification = abandoned ? { verdict: 'rejected', outcome: lostBattle(pending.init) } : await this.verify(ctx, pending, claim, log);
    const { verdict, outcome } = checked;

    let applied: BattleResultResult | null = null;
    let refused: 'battle_state_changed' | 'battle_mismatch' | 'abandoned' | undefined;
    if (abandoned) refused = 'abandoned';
    else if (!isBattleOf(outcome, pending.init)) refused = 'battle_mismatch';
    else {
      try {
        applied = ctx.execute('battle.result', { outcome, basis: pending.basis }) as unknown as BattleResultResult;
      } catch (err) {
        if (!isRuleError(err)) throw err;
        if (err.reason === 'battle_state_changed') refused = 'battle_state_changed';
        else if (err.reason === 'battle_mismatch') refused = 'battle_mismatch';
        else throw err;
      }
    }
    if (refused) this.d.logger.info('battle outcome gives nothing', { playerId: ctx.playerId, battleId, reason: refused, verdict, ...(abandoned ? { startedAt: pending.startedAt } : {}) });

    // The battle is over; a won battle that is not an assault leaves its graves for the grave search.
    const next = withoutBattles(ctx.serverState);
    const mode = pending.init.mode;
    delete next[LAST_WIN_KEY];
    if (applied?.win) next[LAST_WIN_KEY] = { battleId, mode, stage: pending.init.stage, gold: applied.granted.gold } satisfies LastWin as unknown as JsonValue;
    if (applied?.win && mode !== 'assault' && applied.graves.length > 0) {
      const last: LastBattle = { battleId, mode, stage: pending.init.stage, graves: applied.graves.map((g) => ({ name: g.name, cpu_level: g.cpu_level, searched: false })) };
      next[LAST_BATTLE_KEY] = last as unknown as JsonValue;
    }
    ctx.setServerState(next);

    // An assault win captures the zone when the battle is trusted; the capture is decided and stored at the commit.
    let captured: boolean | undefined;
    let captureBlocked: CaptureBlocked | 'not_verified' | undefined;
    let capture = false;
    if (mode === 'assault' && applied?.win) {
      const trusted = verdict === 'verified' || (verdict === 'unverified' && !this.d.config.assault.requireVerifiedBattle);
      if (!trusted) {
        captured = false;
        captureBlocked = 'not_verified';
      } else capture = true;
    }

    const replayId = randomId(this.d.rng, 'r_', 10);
    const level = applied ? applied.level : Number(decodeState(ctx.gameState).player.level);
    const resultNow = (): JsonValue => {
      const result: BattleFinishCommandResult = {
        battleId,
        verdict,
        winner: outcome.winner,
        win: applied?.win ?? false,
        ...(refused ? { refused } : {}),
        endGame: applied ? (cloneJson(applied.endGame) as unknown as { [key: string]: JsonValue }) : null,
        granted: applied ? applied.granted : { ...NOTHING_GRANTED, weapons: [] },
        graves: applied && mode !== 'assault' && applied.win ? applied.graves : [],
        ...(captured !== undefined ? { captured } : {}),
        ...(captureBlocked ? { captureBlocked } : {}),
        level,
        replayId,
      };
      return result as unknown as JsonValue;
    };
    const playerId = ctx.playerId;
    const facts = (): BattleFinishFacts => {
      const simulated = checked.sim?.simulated ?? null;
      const a = comparable(claim) as JsonObject;
      const b = simulated ? (comparable(simulated) as JsonObject) : null;
      const differs = b ? Object.keys(a).filter((k) => canonicalJson(a[k]) !== canonicalJson(b[k])) : [];
      const last = log.turns[log.turns.length - 1];
      const kills = Object.values(outcome.ach_killed ?? {}).reduce((sum, n) => sum + (Number(n) || 0), 0);
      const basis = pending.basis as BattleRewardBasis & { attempt?: number };
      return {
        playerId,
        battleId,
        replayId,
        mode,
        stage: pending.init.stage,
        verdict,
        abandoned,
        refused: refused ?? null,
        win: applied?.win ?? false,
        winner: outcome.winner,
        granted: { gold: applied?.granted.gold ?? 0, diam: applied?.granted.diam ?? 0 },
        claim: a,
        simulated: b,
        differs,
        simError: checked.sim?.error ?? null,
        simMessage: checked.sim?.message ?? null,
        ticks: checked.sim?.ticks ?? last.endTick ?? last.throughTick,
        simulatedTicks: typeof checked.sim?.ticks === 'number',
        ownTurns: log.turns.filter((t) => t.team === 1).length,
        kills,
        annihilators: pending.init.annihilators,
        // *_max is the frontier stage (the next one to win); a lower stage is a replay of a won one.
        newStage: atFrontier(pending),
        attempt: typeof basis.attempt === 'number' ? basis.attempt : null,
        startedAt: pending.startedAt,
        finishedAt: ctx.nowMs,
        simulator: this.simulator !== null,
      };
    };
    const out: ServiceOpOutcome = {
      result: resultNow(),
      source: { battleId, verdict, replayId },
      afterCommit: () => {
        const f = facts();
        for (const l of this.finishListeners) {
          try {
            l(f);
          } catch (err) {
            this.d.logger.error('battle finish listener failed', { playerId, battleId, err });
          }
        }
      },
      // The new state, the replay and a zone capture (zone + the capturer's account) are ONE atomic storage batch:
      // a crash never keeps a capture without the battle's end (or the other way round).
      commit: async (write) => {
        let replay: BattleReplayWrites | null = null;
        try {
          replay = await this.replays.battleWrites(playerId, { replayId, battleId, init: pending.init, seed: pending.seed, log, winner: outcome.winner, verdict, skill: pending.basis.skill });
        } catch (err) {
          this.d.logger.error('battle replay not stored', { playerId, battleId, err });
        }
        const replayOps: StorageBatchOp[] = replay?.ops ?? [];
        if (!capture) {
          await write(replayOps);
          replay?.committed();
          return;
        }
        await this.assault.captureAfterBattle(playerId, { zone: pending.init.stage, expectedStage: pending.init.zoneStage ?? 0, battleId }, async (zoneOps, cap) => {
          captured = cap.captured;
          captureBlocked = cap.blocked ?? undefined;
          out.result = resultNow();
          await write([...zoneOps, ...replayOps]);
          replay?.committed();
        });
      },
    };
    return out;
  }

  /**
   * Re-simulates the battle when a simulator is configured and decides the verdict and the outcome to apply. With a
   * simulator the claim is never applied: a record the simulator refuses is a loss, and a simulation that gives no
   * answer throws UNAVAILABLE (the battle stays pending).
   */
  private async verify(ctx: ServiceOpContext, pending: PendingBattle, claim: BattleOutcome, log: CommandLogData): Promise<Verification> {
    if (!this.simulator) return { verdict: 'unverified', outcome: claim };
    const facts = { playerId: ctx.playerId, battleId: pending.battleId, mode: pending.init.mode, stage: pending.init.stage };
    const sim = await this.simulate(this.simulator, pending, log);
    if (sim === null) {
      this.d.logger.warn('battle not verified yet: no answer from the simulator in time, the battle stays pending', { ...facts, error: 'no_answer', waitedMs: this.simWaitMs });
      throw simulationUnavailable(pending.battleId, 'no_answer');
    }
    if (!sim.ok) {
      if (RECORD_SIM_ERRORS.has(sim.error)) {
        this.d.logger.warn('battle claim rejected: the record cannot be re-simulated, the battle is lost', { ...facts, error: sim.error, message: sim.message, durationMs: sim.durationMs });
        return { verdict: 'rejected', outcome: lostBattle(pending.init), sim: { ticks: null, error: sim.error, message: sim.message, simulated: null } };
      }
      this.d.logger.warn('battle not verified yet: the simulation failed, the battle stays pending', { ...facts, error: sim.error, message: sim.message, durationMs: sim.durationMs });
      throw simulationUnavailable(pending.battleId, sim.error);
    }
    let simulated: BattleOutcome;
    try {
      simulated = parseBattleOutcome(sim.outcome);
    } catch (err) {
      this.d.logger.error('battle not verified yet: the simulator returned a malformed outcome, the battle stays pending', { ...facts, err });
      throw simulationUnavailable(pending.battleId, 'crashed');
    }
    if (sameOutcome(simulated, claim)) {
      this.d.logger.info('battle verified', { ...facts, winner: simulated.winner, ticks: sim.ticks, durationMs: sim.durationMs });
      return { verdict: 'verified', outcome: simulated, sim: { ticks: sim.ticks, error: null, message: null, simulated } };
    }
    this.d.logger.warn('battle claim rejected: the simulation differs, the simulated outcome is applied', {
      ...facts,
      claimed: comparable(claim),
      simulated: comparable(simulated),
      ticks: sim.ticks,
    });
    return { verdict: 'rejected', outcome: simulated, sim: { ticks: sim.ticks, error: null, message: null, simulated } };
  }

  /**
   * The simulator's answer for the pending battle and this log (errors of simulator.simulate turned into 'crashed'),
   * or null when none came within simWaitMs. A simulation that outlives the wait keeps running: the retried finish
   * of the same battle and log joins it, or takes the answer it gave in the meantime, so a retry never queues a second
   * simulation of the same record.
   */
  private async simulate(simulator: BattleSimulator, pending: PendingBattle, log: CommandLogData): Promise<SimulationResult | null> {
    const key = `${pending.battleId}:${sha256Hex(canonicalJson(log as unknown as JsonValue))}`;
    let run = this.simulations.get(key);
    if (!run) {
      // One simulation per battle is remembered: another log of the same battle replaces the older one.
      for (const k of [...this.simulations.keys()]) if (k.startsWith(`${pending.battleId}:`)) this.simulations.delete(k);
      const t0 = this.d.clock.now();
      const record: BattleRecord = { init: pending.init, seed: pending.seed, log };
      const started = Promise.resolve()
        .then(() => simulator.simulate(record, { maxTicks: this.maxTicksFor(pending.init.mode), timeoutMs: this.simTimeoutMs }))
        .catch((err: unknown): SimulationResult => ({ ok: false, error: 'crashed', message: err instanceof Error ? err.message : String(err), durationMs: this.d.clock.now() - t0 }));
      this.simulations.set(key, started);
      void started.then(() => {
        const t = setTimeout(() => {
          if (this.simulations.get(key) === started) this.simulations.delete(key);
        }, BATTLE_SIM_ANSWER_KEEP_MS);
        t.unref?.();
      });
      run = started;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const guard = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), this.simWaitMs);
    });
    try {
      const answer = await Promise.race([run, guard]);
      // Taken: a later finish of this battle (after a failure the server caused) simulates again.
      if (answer !== null && this.simulations.get(key) === run) this.simulations.delete(key);
      return answer;
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  }

  // ---------------------------------------------------------------------------------------------- battle.forfeit

  private forfeit(ctx: ServiceOpContext, args: JsonObject): ServiceOpOutcome {
    const o = argsObject(args, ['battleId']);
    const battleId = stringArg(o, 'battleId', { maxLength: 64, pattern: BATTLE_ID_PATTERN });
    const pending = this.requirePending(ctx, battleId);
    ctx.setServerState(withoutBattles(ctx.serverState));
    const result: BattleForfeitCommandResult = { battleId, forfeited: true };
    const playerId = ctx.playerId;
    const nowMs = ctx.nowMs;
    return { result: result as unknown as JsonValue, afterCommit: () => this.lost(playerId, pending, 'forfeited', nowMs) };
  }

  // ---------------------------------------------------------------------------------------------- grave.search

  private searchGrave(ctx: ServiceOpContext, args: JsonObject): ServiceOpOutcome {
    const o = argsObject(args, ['index']);
    const last = lastBattleOf(ctx.serverState);
    if (!last) throw new RuleError('not_available', { problem: 'no graves to search: they come with a won battle' });
    const r = ctx.execute('grave.search', { index: o.index, battle: { mode: last.mode, stage: last.stage, graves: last.graves } }) as unknown as GraveResult;
    const graves = last.graves.map((g, i) => (i === r.index - 1 ? { ...g, searched: true } : g));
    ctx.setServerState({ ...ctx.serverState, [LAST_BATTLE_KEY]: { ...last, graves } as unknown as JsonValue });
    return { result: r as unknown as JsonValue };
  }
}
